import {ActionMode} from "../../constants/ActionMode";
import {Formik} from "formik";
import {FVPTextInput} from "../../components/formField";
import CFAdminConfirmDialog from "../../components/CFAdminConfirmDialog";
import React, {useState} from "react";
import ExcludedErrorVinService from "../../services/ExcludedErrorVinService";
import * as Yup from "yup";

const schema = Yup.object().shape({
    excludedErrorVin: Yup.string().length(17, "Excluded Error VIN must be exactly 17 characters"),
    note: Yup.string()
        .max(900, "Admin Desc is allowed maximum of 900 characters"),
});

type CFAdminExcludedErrorVINProps = Readonly<{
    id?: number;
    isDelete: boolean
    mode?: ActionMode;
    excludedErrorVin: string;
    note: string;
    userKey: string;
    modified: string;
    usedFlag: boolean;
    toggleModal: (isAdd?: boolean) => void;
    refreshData: () => Promise<void>;
    successMessage: (value: any) => Promise<void>;
    errorMessage: (value: any) => Promise<void>;
}>;

function CFAdminExcludedErrorVinForm({id, mode, isDelete, excludedErrorVin, note, userKey, usedFlag, modified, toggleModal, refreshData, successMessage, errorMessage}: CFAdminExcludedErrorVINProps) {

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    const saveExcludedErrorVIN = (values: any) => {
        const data: any = {
            "excludedErrorVin": values.excludedErrorVin,
            "note": values.note,
            "modified": values.modified,
            "userKey": values.userKey,
            "usedFlag": values.usedFlag,
        };

        ExcludedErrorVinService.createExcludedError(data)
            .then(response => {
                successMessage(`Excluded Error VIN: ${response.data.excludedErrorVin} added successfully.`);
                refreshData();
                toggleModal();
            })
            .catch(e => {
                console.error(e);
                errorMessage("Error while adding Excluded Error VIN!.");
            });
    };

    const updateExcludedErrorVIN = (values: any) => {
        const data = {
            "excludedErrorVin": values.excludedErrorVin,
            "note": values.note,
            "modified": values.modified,
            "userKey": values.userKey,
            "usedFlag": values.usedFlag,
        };

        ExcludedErrorVinService.updateExcludedError(values.id, data)
            .then(() => {
                successMessage(values.excludedErrorVin+" updated successfully.");
                refreshData();
                toggleModal();
            })
            .catch(e => {
                console.error(e);
                errorMessage("Error while updating Excluded Error VIN!.");
            });
    };

    const deleteExcludedErrorVIN = () => {
        if (id) {
            ExcludedErrorVinService.deleteExcludedError(id)
                .then(() => {
                    successMessage(`Excluded Error VIN: ${excludedErrorVin} deleted successfully.`);
                    refreshData();
                    toggleModal();
                })
                .catch(e => {
                    errorMessage("Error while deleting Excluded Error VIN!");
                });
        } else {
            errorMessage("Error while deleting Excluded Error VIN!");
        }
    };

    return (
        <>
            <Formik
                validationSchema={schema}
                initialValues={{ id: id, isDelete: isDelete, mode: mode, excludedErrorVin: excludedErrorVin, note: note, userKey: userKey, modified: modified, usedFlag: usedFlag }}
                onSubmit={(values) => {
                    if(isDelete){
                        setIsDeleteConfirmOpen(true);
                    } else if (id !== undefined && id > 0) {
                        updateExcludedErrorVIN(values);
                    } else {
                        saveExcludedErrorVIN(values);
                    }
                }}
            >
                {({
                      values,
                      handleSubmit,
                      isValid,
                      dirty
                  }) => (
                    <div className="fvp-form">
                        <div>
                            <form data-testid="SubmitForm" noValidate onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="fvp-form__group">
                                        <div className="fvp-form__subgroup">
                                            <FVPTextInput label="Excluded Error VIN:" name="excludedErrorVin"
                                                          required readOnly={usedFlag ||( values.id !== undefined && values.id !== 0)}/>
                                            <FVPTextInput label="Note:" name="note" required readOnly={usedFlag}/>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer button-frame footer-buttons">
                                    <button data-testid="Cancel" className="fvp-button--outline" onClick={() => toggleModal()}>
                                        <i className="fvp-left-chevron" />{' '}
                                        Cancel
                                    </button>
                                    {isDelete ? (
                                        <button data-testid="Delete" className="fvp-button-danger--outline button-right-align" type="submit">
                                            Delete{' '}
                                            <i className="fvp-right-chevron" />
                                        </button>
                                    ) : (
                                        <button data-testid="Save" className="fvp-button button-right-align" type="submit" disabled={!(dirty && isValid)}>
                                            {values.id === undefined || values.id === 0 ? 'Save' : 'Update'}
                                            <i className="fvp-right-chevron" />
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </Formik>
            <CFAdminConfirmDialog
                isOpen={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                onConfirm={deleteExcludedErrorVIN}
                title={"Delete Excluded Error VIN: " + excludedErrorVin}
                text="Are you sure you want to delete this Excluded Error VIN?"
                overlayClassName="delete-modal-overlay"
            />
        </>
    );
}

export default CFAdminExcludedErrorVinForm;